import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import CompanionCard, { prettyCity } from "@/components/CompanionCard";
import { Reveal } from "@/components/Reveal";

/**
 * SEO-friendly town/city landing page.
 * Route: /locations/:citySlug  or  /locations/:citySlug/:town
 */
export default function LocationLanding() {
  const { city, town } = useParams();
  const [companions, setCompanions] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { city, ...(town ? { town: decodeURIComponent(town) } : {}) };
    api.get("/companions", { params }).then((r) => setCompanions(r.data)).catch(() => {});
    const label = town ? `${decodeURIComponent(town)}, ${prettyCity(city)}` : prettyCity(city);
    document.title = `${label} Companions — Vixera`;
  }, [city, town]);

  const cityInfo = useMemo(() => {
    for (const p of locations) {
      const c = (p.cities || []).find((x) => x.slug === city);
      if (c) return { province: p.name, ...c };
    }
    return null;
  }, [locations, city]);

  const label = town ? `${decodeURIComponent(town)}, ${prettyCity(city)}` : prettyCity(city);

  return (
    <div className="pt-28 md:pt-36" data-testid="location-landing">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <p className="mb-3 text-xs uppercase tracking-luxe text-velvet-gold">
            {cityInfo?.province || "South Africa"}
          </p>
          <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-7xl">
            Companions in <span className="italic text-velvet-rose">{label}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm font-light text-white/50">
            Verified companions, discreet meetings, and refined evenings across {label}. Every profile on Vixera is
            reviewed for authenticity — because your night deserves nothing less.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/directory" className="rounded-full border border-white/15 px-5 py-2 text-[10px] uppercase tracking-luxe text-white/70 hover:border-velvet-gold hover:text-velvet-gold">
              Full directory
            </Link>
            {cityInfo?.towns?.slice(0, 6).map((t) => (
              <Link
                key={t}
                to={`/locations/${city}/${encodeURIComponent(t)}`}
                className={`rounded-full border px-5 py-2 text-[10px] uppercase tracking-luxe ${
                  decodeURIComponent(town || "") === t
                    ? "border-velvet-gold text-velvet-gold"
                    : "border-white/15 text-white/60 hover:border-velvet-rose hover:text-velvet-rose"
                }`}
              >
                {t}
              </Link>
            ))}
          </div>
        </Reveal>

        <div className="mt-16 mb-6 flex items-baseline justify-between">
          <p className="text-xs uppercase tracking-luxe text-white/50">
            {companions.length} companion{companions.length !== 1 ? "s" : ""} available
          </p>
        </div>

        {companions.length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <p className="font-heading text-2xl font-light text-white/50">
              No companions in {label} right now.
            </p>
            <Link to="/directory" className="mt-6 inline-block text-xs uppercase tracking-luxe text-velvet-gold">
              Browse the full directory →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {companions.map((c, i) => (
              <CompanionCard key={c.id} companion={c} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
